import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Check, Loader2, ArrowLeft, ShieldCheck, CalendarClock, XCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FEATURES = [
  "Full S&P ranked scanner (long + short)",
  "7-gate detail dashboards with entry/stop/targets",
  "Real-time watchlist triggers & state alerts",
  "Backtest engine — win rate, avg R, expectancy by grade",
  "Position sizing from your account risk",
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—");

export default function Paywall() {
  const nav = useNavigate();
  const { access, refreshAccess, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await client.get("/subscription");
      setSub(data.subscription);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subscribed = access?.reason === "subscription";
  const trial = access?.reason === "trial";
  const locked = access && !access.access;
  const canceling = sub?.cancel_at_period_end;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/subscription/cancel");
      setSub(data.subscription);
      await refreshAccess();
      toast("Subscription will end at the period close");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
    finally { setBusy(false); }
  };

  const doResume = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/subscription/resume");
      setSub(data.subscription);
      await refreshAccess();
      toast.success("Subscription resumed — auto-renew is back on");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resume"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 py-10 text-white">
      <div className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="relative mx-auto max-w-lg sg-fade">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={22} className="text-[#00F0FF]" strokeWidth={2.5} />
            <span className="font-head text-xl font-black tracking-tight">KLONDIKE<span className="text-[#00F0FF]">SWING</span></span>
          </div>
          {access?.access ? (
            <button data-testid="paywall-back" onClick={() => nav("/")} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white">
              <ArrowLeft size={14} /> Back to app
            </button>
          ) : (
            <button onClick={async () => { await logout(); nav("/login"); }} className="text-xs text-gray-500 hover:text-gray-300">Sign out</button>
          )}
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="animate-spin text-[#00F0FF]" /></div>
        ) : subscribed ? (
          <div className="border border-white/10 bg-[#111111] p-7" data-testid="billing-panel">
            <div className="flex items-center gap-2 text-[#00FF66]">
              <ShieldCheck size={18} /> <span className="font-head text-lg font-bold">KlondikeSwing Pro — active</span>
            </div>
            <div className="mt-5 space-y-3 font-mono-sg text-sm">
              <div className="flex items-center justify-between border border-white/5 bg-black/40 px-3 py-2.5">
                <span className="text-gray-500">Plan</span><span className="text-white">$20 / month</span>
              </div>
              <div className="flex items-center justify-between border border-white/5 bg-black/40 px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-gray-500"><CalendarClock size={14} /> {canceling ? "Access ends" : "Renews on"}</span>
                <span data-testid="renewal-date" className="text-white">{fmtDate(sub?.current_period_end)}</span>
              </div>
              <div className="flex items-center justify-between border border-white/5 bg-black/40 px-3 py-2.5">
                <span className="text-gray-500">Auto-renew</span>
                <span className={canceling ? "text-[#FF7F00]" : "text-[#00FF66]"}>{canceling ? "OFF (canceling)" : "ON"}</span>
              </div>
            </div>

            {canceling ? (
              <>
                <p className="mt-4 text-xs text-[#FF7F00]">Your plan is set to cancel. You'll keep Pro access until {fmtDate(sub?.current_period_end)}.</p>
                <button data-testid="resume-btn" onClick={doResume} disabled={busy}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-sm bg-[#00FF66] py-2.5 font-mono-sg text-sm font-bold text-black transition-colors duration-200 hover:bg-[#00FF66]/80 disabled:opacity-50">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} RESUME SUBSCRIPTION
                </button>
              </>
            ) : (
              <button data-testid="cancel-btn" onClick={doCancel} disabled={busy}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-sm border border-[#FF3B30]/40 py-2.5 font-mono-sg text-sm font-bold text-[#FF3B30] transition-colors duration-200 hover:bg-[#FF3B30]/10 disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />} CANCEL SUBSCRIPTION
              </button>
            )}
            <p className="mt-3 text-center font-mono-sg text-[11px] text-gray-600">Canceling stops auto-renew; you keep access until the period ends.</p>
          </div>
        ) : (
          <div className="border border-white/10 bg-[#111111] p-7">
            <div className="inline-flex items-center gap-2 rounded-sm border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-2.5 py-1 font-mono-sg text-xs text-[#00F0FF]">
              {locked ? "Free trial ended" : trial ? `${access.trial_days_left} day${access.trial_days_left === 1 ? "" : "s"} left in trial` : "KlondikeSwing Pro"}
            </div>
            <h1 className="mt-4 font-head text-3xl font-black tracking-tight">Unlock KlondikeSwing Pro</h1>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-head text-4xl font-black text-[#00F0FF]">$20</span>
              <span className="font-mono-sg text-sm text-gray-500">/ month · cancel anytime</span>
            </div>
            <ul className="mt-6 space-y-2.5">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-gray-300">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#00FF66]" /> {f}
                </li>
              ))}
            </ul>
            <button data-testid="subscribe-btn" onClick={subscribe} disabled={busy}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-sm bg-[#00F0FF] py-3 font-mono-sg text-sm font-bold text-black transition-colors duration-200 hover:bg-[#00F0FF]/80 disabled:opacity-50">
              {busy && <Loader2 size={15} className="animate-spin" />}
              {busy ? "REDIRECTING…" : "SUBSCRIBE — $20/MO"}
            </button>
            <p className="mt-3 text-center font-mono-sg text-[11px] text-gray-600">Secure checkout by Stripe · test card 4242 4242 4242 4242</p>
          </div>
        )}
      </div>
    </div>
  );
}
